'use client';

import React from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Mail } from 'lucide-react';
import { useSupportContact, waLink, SUPPORT_EMAIL } from '@/hooks/useSupportContact';
import { useIsNativeApp } from '@/lib/platform';

export default function Footer() {
  const { t } = useTranslation('common');
  const { whatsapp } = useSupportContact();
  // K-0071: the cancellation policy is a page about a monthly charge. The law
  // requires it to be published and reachable where the subscription is taken
  // out — that is the website, and it stays there. Inside the app the link goes
  // away with everything else that talks about money.
  const isNative = useIsNativeApp();

  return (
    <footer className="w-full border-t border-border/60 bg-card/50 backdrop-blur-sm mt-auto">
      <div className="max-w-[1400px] mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Brand */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-black text-foreground">YofiPlace</span>
            <span>© {new Date().getFullYear()}</span>
          </div>

          {/* Legal links. Wrapping is not optional: four translated labels on
              one line fit Hebrew and nothing else — in French the row ran off
              both edges of a 375px screen, and this footer is on every page the
              client sees too. */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
            <Link
              href="/about"
              className="text-muted-foreground hover:text-primary transition-colors font-medium"
            >
              {t('nav.about')}
            </Link>
            <span className="text-border">|</span>
            <Link
              href="/terms"
              className="text-muted-foreground hover:text-primary transition-colors font-medium"
            >
              {t('footer.terms')}
            </Link>
            <span className="text-border">|</span>
            <Link
              href="/privacy"
              className="text-muted-foreground hover:text-primary transition-colors font-medium"
            >
              {t('footer.privacy')}
            </Link>
            <span className="text-border">|</span>
            <Link
              href="/accessibility"
              className="text-muted-foreground hover:text-primary transition-colors font-medium"
            >
              {t('footer.accessibility')}
            </Link>
            {/* Required to be published and reachable for a subscription that
                renews monthly — and it has to be findable without logging in. */}
            {isNative === false && (
              <>
                <span className="text-border">|</span>
                <Link
                  href="/cancellation-policy"
                  className="text-muted-foreground hover:text-primary transition-colors font-medium"
                >
                  {t('footer.cancellation')}
                </Link>
              </>
            )}
          </div>

          {/* K-0049: связь с фирмой — WhatsApp главный, почта второй строкой.
              Телефона для звонков у фирмы нет и не будет. */}
          <div className="flex items-center gap-4 text-sm">
            {whatsapp && (
              <a href={waLink(whatsapp, 'שלום, הגעתי מ-YofiPlace ויש לי שאלה')}
                 target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-1.5 font-bold text-emerald-600 hover:text-emerald-500 transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
            <a href={`mailto:${SUPPORT_EMAIL}`}
               className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors" dir="ltr">
              <Mail className="w-3.5 h-3.5" /> {SUPPORT_EMAIL}
            </a>
          </div>

          {/* Строка о площадке.
              Было «נעשה עם ❤ בישראל» — «сделано с любовью в Израиле» с сердечком.
              Такое пишут на странице одиночки, который сделал сайт по вечерам,
              и мастер, выбирающий, кому доверить запись клиентов, читает это
              именно так. Заменено на строку о том, чем площадка является:
              израильская площадка записи, и сколько на ней мастеров. */}
          <div className="text-xs text-muted-foreground/60 text-center">
            {t('footer.tagline')}
          </div>
        </div>
      </div>
    </footer>
  );
}
